import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ErrorMessage from './common/ErrorMessage';
import FormField from './common/FormField';
import { apiPost, apiPut } from '../utils/api';
import { getLevelDisplayName, getStatusDisplayName } from '../utils/formatters';
import { useApi } from '../hooks/useApi';
import './UserProfile.css';

// 담당자 태그 색상 팔레트 (backend/user_colors.py 와 값 동기화)
// 색상환에 고르게 퍼진 12색 — 서로 확실히 구별되고, 흰색 글자 대비 ≥ 4.3 확보
const TAG_COLOR_PALETTE = [
  '#E03131', '#D9480F', '#9C6B1A', '#4C8000',
  '#2B8A3E', '#087F5B', '#0B7285', '#1971C2',
  '#3B5BDB', '#6741D9', '#9C36B5', '#C2255C',
];

function UserProfile() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const { loading: isLoading, callApi } = useApi();

  const [selectedColor, setSelectedColor] = useState(user?.color || '#1971C2');
  const [colorMessage, setColorMessage] = useState('');
  const { loading: isColorSaving, callApi: callColorApi } = useApi();

  // 사용자 정보(색상)가 비동기로 로드되면 선택 색상 동기화
  useEffect(() => {
    if (user?.color) setSelectedColor(user.color);
  }, [user?.color]);

  const handleSaveColor = async (color) => {
    setColorMessage('');
    try {
      await callColorApi(async () => {
        const data = await apiPut('/api/auth/me/color', { color });
        setSelectedColor(color);
        if (data?.user) updateUser({ color: data.user.color });
        setColorMessage('담당자 색상이 변경되었습니다.');
        return data;
      });
    } catch (error) {
      setColorMessage(error.message || '색상 변경에 실패했습니다.');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setMessage('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
      return;
    }

    if (newPassword.length < 4) {
      setMessage('새 비밀번호는 최소 4자 이상이어야 합니다.');
      return;
    }

    setMessage('');

    try {
      await callApi(async () => {
        const data = await apiPost('/api/auth/change-password', {
          current_password: currentPassword,
          new_password: newPassword
        });

        setMessage('비밀번호가 성공적으로 변경되었습니다.');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        return data;
      });
    } catch (error) {
      setMessage(error.message || '비밀번호 변경에 실패했습니다.');
    }
  };

  return (
    <div className="profile-page">
      <div className="profile-header">
        <button onClick={() => navigate('/payroll')} className="profile-back" title="뒤로가기">←</button>
        <h2>내 정보</h2>
      </div>

      <div className="profile-grid">
        {/* 사용자 정보 */}
        <section className="profile-card">
          <h3 className="profile-card-title">사용자 정보</h3>
          <dl className="profile-fields">
            <div className="profile-field">
              <dt>사용자명</dt>
              <dd>{user?.id}</dd>
            </div>
            <div className="profile-field">
              <dt>이름</dt>
              <dd>{user?.name}</dd>
            </div>
            <div className="profile-field">
              <dt>권한</dt>
              <dd>{getLevelDisplayName(user?.level)}</dd>
            </div>
            <div className="profile-field">
              <dt>상태</dt>
              <dd>{getStatusDisplayName(user?.status)}</dd>
            </div>
          </dl>
        </section>

        {/* 담당자 태그 색상 */}
        <section className="profile-card">
          <h3 className="profile-card-title">담당자 태그 색상</h3>
          <p className="profile-card-desc">기업 검색 화면에서 내 담당자 태그에 표시됩니다.</p>
          <div className="tag-color-preview-row">
            <span className="tag-color-preview" style={{ backgroundColor: selectedColor }}>
              {user?.name || user?.id}
            </span>
            <span className="tag-color-code">{selectedColor}</span>
          </div>
          <div className="tag-color-swatches">
            {TAG_COLOR_PALETTE.map(color => (
              <button
                key={color}
                type="button"
                className={`tag-color-swatch ${selectedColor?.toLowerCase() === color.toLowerCase() ? 'selected' : ''}`}
                style={{ backgroundColor: color }}
                title={color}
                disabled={isColorSaving}
                onClick={() => handleSaveColor(color)}
              />
            ))}
            <label className="tag-color-custom" title="직접 선택">
              <input
                type="color"
                value={selectedColor}
                disabled={isColorSaving}
                onChange={(e) => setSelectedColor(e.target.value)}
                onBlur={(e) => handleSaveColor(e.target.value)}
              />
              <span className="tag-color-custom-icon">🎨</span>
            </label>
          </div>
          {colorMessage && (
            <ErrorMessage
              message={colorMessage}
              type={colorMessage.includes('변경되었습니다') ? 'success' : 'error'}
            />
          )}
        </section>
      </div>

      {/* 비밀번호 변경 */}
      <section className="profile-card profile-card-password">
        <h3 className="profile-card-title">비밀번호 변경</h3>
        <form onSubmit={handleChangePassword} className="password-form">
          <div className="password-fields">
            <FormField
              label="현재 비밀번호"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
            <FormField
              label="새 비밀번호"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <FormField
              label="새 비밀번호 확인"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>

          {message && (
            <ErrorMessage
              message={message}
              type={message.includes('성공') ? 'success' : 'error'}
            />
          )}

          <div className="password-actions">
            <button
              type="submit"
              className="profile-submit-btn"
              disabled={isLoading}
            >
              {isLoading ? '변경 중...' : '비밀번호 변경'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default UserProfile;